import Bugsnag from '@bugsnag/js';
import { UNIVERSAL_ENV_VARS } from '../../constants/environmentVariables';
import { getMykissEnv } from '../environment/getMykissEnv';
import { config } from '../../../config/bugsnag.config';
import { isBrowser } from '../environment/isBrowser';
import { log, reportAPIError, reportApolloError, reportGraphQLError } from './errorUtils';
import { ApolloError } from '@apollo/client';
import { GraphQLError } from 'graphql';

const releaseStage = getMykissEnv();

export const bugsnagServerClient = Bugsnag.createClient({
  apiKey: UNIVERSAL_ENV_VARS.BUGSNAG_API_KEY,
  releaseStage,
  appVersion: UNIVERSAL_ENV_VARS.COMMIT_SHA,
  onError: function onError(report) {
    if (report.request.url) {
      report.request.decodedUrl = decodeURI(report.request.url);
    }
    // Output the error to console if running node in a dev environment.
    if (!config.stages.includes(releaseStage)) {
      log(report);
      return false;
    }

    if (!isBrowser()) {
      log(report);
    }

    return true;
  },
});

/*
 * This function is used to report errors that occur on the server.
 * Do not use this function to report errors in client components.
 * Use `reportMykissClientError` instead.
 */
export function reportMykissServerError(
  error?: Error | Response,
  context?: string,
  severity?: 'info' | 'warning',
  otherData?: object,
): void {
  // The below check of error.text is a replacement for `error instanceof Response`, as the Response
  // class is not available in the Node environment. A Fetch API response should have a `text`
  // function (we use it in `reportAPIError`), so we use it's existance to decide the error is from
  // a Fetch request.
  // @ts-ignore
  if (error?.text) {
    reportAPIError(bugsnagServerClient, error as Response, severity, otherData, context);
  } else if (error instanceof ApolloError) {
    reportApolloError(bugsnagServerClient, error, severity, otherData, context);
  } else if (error instanceof GraphQLError) {
    reportGraphQLError(bugsnagServerClient, error, severity, otherData, context);
  } else {
    bugsnagServerClient.notify(error as Error, event => {
      event.context = context;
      if ((error as Error).name === 'NotFoundError') {
        event.severity = severity ?? 'info';
      } else {
        event.severity = severity ?? 'error';
      }

      if (otherData) {
        event.addMetadata('other', otherData);
      }
      return true;
    });
  }
}
